import React from 'react';
import { CalendarIcon } from 'lucide-react';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/admin/components/ui/dialog';
import { Input } from '@/admin/components/ui/input';
import { Label } from '@/admin/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  createAdminRedeemCode,
  disableAdminRedeemCode,
  fetchAdminAddons,
  fetchAdminRedeemCodes,
  type AdminAddonPlan,
  type AdminRedeemCode,
} from '@/admin/lib/api';

const statusVariant = (
  status: string
): 'default' | 'secondary' | 'destructive' => {
  switch (status.toLowerCase()) {
    case 'disabled':
      return 'destructive';
    case 'expired':
      return 'secondary';
    default:
      return 'default';
  }
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type RedeemFormValues = {
  code: string;
  planCode: string;
  ownerUserId: string;
  maxUses: string;
  expiresAt: string;
};

export const RedeemCodesPage: React.FC = () => {
  const [codes, setCodes] = React.useState<AdminRedeemCode[]>([]);
  const [addons, setAddons] = React.useState<AdminAddonPlan[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [disablingId, setDisablingId] = React.useState<string | null>(null);
  const expiresAtInputRef = React.useRef<HTMLInputElement | null>(null);

  const [formValues, setFormValues] = React.useState<RedeemFormValues>({
    code: '',
    planCode: '',
    ownerUserId: '',
    maxUses: '',
    expiresAt: '',
  });

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [codesResponse, addonsResponse] = await Promise.all([
        fetchAdminRedeemCodes(),
        fetchAdminAddons(200, 0),
      ]);
      setCodes(codesResponse.codes);
      setAddons(addonsResponse.addons);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load codes');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormValues({
      code: '',
      planCode: addons[0]?.plan_code ?? '',
      ownerUserId: '',
      maxUses: '',
      expiresAt: '',
    });
  };

  const handleOpenChange = (open: boolean) => {
    setCreateOpen(open);
    if (open) {
      resetForm();
    }
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formValues.code.trim()) {
      setError('Code is required.');
      return;
    }
    if (!formValues.planCode.trim()) {
      setError('Plan code is required.');
      return;
    }

    const maxUsesValue = formValues.maxUses.trim();
    const maxUses = maxUsesValue ? Number.parseInt(maxUsesValue, 10) : 0;
    if (maxUsesValue && (!Number.isFinite(maxUses) || maxUses <= 0)) {
      setError('Max uses must be a positive number.');
      return;
    }

    let expiresAt: string | null = null;
    if (formValues.expiresAt) {
      const parsed = new Date(formValues.expiresAt);
      if (Number.isNaN(parsed.getTime())) {
        setError('Expires at must be a valid date.');
        return;
      }
      expiresAt = parsed.toISOString();
    }

    setCreating(true);
    try {
      await createAdminRedeemCode({
        code: formValues.code.trim(),
        plan_code: formValues.planCode.trim(),
        owner_user_id: formValues.ownerUserId.trim() || null,
        max_uses: maxUses,
        expires_at: expiresAt,
      });
      setCreateOpen(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const handleDisable = async (id: string) => {
    setDisablingId(id);
    try {
      await disableAdminRedeemCode(id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable code');
    } finally {
      setDisablingId(null);
    }
  };

  const openExpiresAtPicker = () => {
    const input = expiresAtInputRef.current;
    if (!input) return;
    const wasReadOnly = input.readOnly;
    if (wasReadOnly) {
      input.readOnly = false;
    }
    let opened = false;
    const picker = (input as HTMLInputElement & { showPicker?: () => void })
      .showPicker;
    if (picker) {
      try {
        picker.call(input);
        opened = true;
      } catch {
        opened = false;
      }
    }
    if (!opened) {
      input.focus();
      input.click();
    }
    if (wasReadOnly) {
      window.setTimeout(() => {
        input.readOnly = true;
      }, 0);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-auto py-4 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Redeem Codes</h1>
            <p className="text-sm text-muted-foreground">
              Generate and manage credits redeem codes.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadData}>
              Refresh
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              New code
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="px-4 lg:px-6">
          <Card className="border-destructive/40">
            <CardHeader>
              <CardTitle className="text-base">Action needed</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      ) : null}

      <div className="px-4 lg:px-6">
        <Card>
          <CardHeader>
            <CardTitle>Active redeem codes</CardTitle>
            <CardDescription>
              Track usage, expiry, and status for issued codes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uses</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map(code => {
                  const maxUses = code.max_uses ?? null;
                  const usesLabel = maxUses
                    ? `${code.used_count}/${maxUses}`
                    : `${code.used_count}/∞`;
                  return (
                    <TableRow key={code.id}>
                      <TableCell className="font-medium">{code.code}</TableCell>
                      <TableCell>{code.plan_code}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(code.status)}>
                          {code.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{usesLabel}</TableCell>
                      <TableCell>{formatDateTime(code.expires_at)}</TableCell>
                      <TableCell className="text-right">
                        {code.status === 'active' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDisable(code.id)}
                            disabled={disablingId === code.id}
                          >
                            {disablingId === code.id ? 'Disabling…' : 'Disable'}
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" disabled>
                            Disabled
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!loading && codes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm">
                      No redeem codes created yet.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Dialog open={createOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New redeem code</DialogTitle>
            <DialogDescription>
              Create a code tied to an add-on plan.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={formValues.code}
                onChange={event =>
                  setFormValues(prev => ({ ...prev, code: event.target.value }))
                }
                placeholder="WELCOME-2024"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan_code">Plan</Label>
              <Select
                value={formValues.planCode}
                onValueChange={value =>
                  setFormValues(prev => ({ ...prev, planCode: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an add-on" />
                </SelectTrigger>
                <SelectContent>
                  {addons.map(addon => (
                    <SelectItem key={addon.plan_code} value={addon.plan_code}>
                      {addon.plan_code} ({addon.addon_source})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="max_uses">Max uses</Label>
                <Input
                  id="max_uses"
                  type="number"
                  min={1}
                  value={formValues.maxUses}
                  onChange={event =>
                    setFormValues(prev => ({
                      ...prev,
                      maxUses: event.target.value,
                    }))
                  }
                  placeholder="10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expires_at">Expires at</Label>
                <div className="flex items-center gap-2">
                  <Input
                    ref={expiresAtInputRef}
                    id="expires_at"
                    type="datetime-local"
                    value={formValues.expiresAt}
                    className="flex-1"
                    inputMode="none"
                    readOnly
                    onKeyDown={event => {
                      if (event.key === 'Tab') return;
                      event.preventDefault();
                    }}
                    onPaste={event => event.preventDefault()}
                    onDrop={event => event.preventDefault()}
                    onChange={event =>
                      setFormValues(prev => ({
                        ...prev,
                        expiresAt: event.target.value,
                      }))
                    }
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Pick expiration date"
                    onClick={openExpiresAtPicker}
                  >
                    <CalendarIcon />
                  </Button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="owner_user_id">Owner user ID (optional)</Label>
              <Input
                id="owner_user_id"
                value={formValues.ownerUserId}
                onChange={event =>
                  setFormValues(prev => ({
                    ...prev,
                    ownerUserId: event.target.value,
                  }))
                }
                placeholder="User UUID"
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
